import { Box, Flex, Stack } from "@chakra-ui/react";
import {
  cleanObjectUndefinedValues,
  DownloadIcon,
  FilterIcon,
  VeriTable,
} from "@verivest/shared-components";
import { VeriSearchInput } from "@verivest/shared-components/src/components/common/VeriSearchInput";
import { Renderers } from "@verivest/shared-components/src/components/common/VeriTable/renderers";
import { VeriAccountSelect } from "@verivest/shared-components/src/components/form-controls/model-controls/VeriAccountSelect";
import { VeriOpportunitySelect } from "@verivest/shared-components/src/components/form-controls/model-controls/VeriOpportunitySelect";
import React from "react";
import { DocumentsTableProps } from "./DocumentsTable.types";

import { HookFormDateRangeInput } from "@verivest/shared-components/src/components/form-controls/VeriDateRangeInput/HookFormDateRangeInput";
import { Button, SearchIcon } from "../../../../general";
import useDocumentModel from "@verivest/shared-components/src/api/hooks/models/useDocumentModel";
import { cn } from "@verivest/shared-components/src/utils/css";
import { useIsMobile } from "../../../../../hooks/useIsMobile";

export const DocumentsTable: React.FC<DocumentsTableProps> = ({
  form,
  onClearFilters,
  startDownloadJob,
  manager,
  tagId,
  hasAnyFilter,
  ...rest
}) => {
  const documentModel = useDocumentModel();
  const searchQuery = form.watch("searchQuery");
  const dates = String(form.watch("date") || "").split(",");
  const filterAccount = form.watch("account");
  const filterOpportunity = form.watch("opportunity");

  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);
  const isMobile = useIsMobile();

  return (
    <Box className={"pb-16 sm:mb-0"} {...rest}>
      <Flex className={"gap-2 items-center mb-4"}>
        <Box className={"flex-grow"}>
          <VeriSearchInput
            onSearchChange={(query) => form.setValue("searchQuery", query)}
            searchValue={searchQuery}
            label={"Documents"}
          />
        </Box>
        <Box className={"p-4 block sm:hidden"}>
          <Button
            onClick={setIsMobileFiltersOpen.bind(null, !isMobileFiltersOpen)}
            empty
          >
            <FilterIcon color={"ink"} boxSize={6} />
          </Button>
        </Box>
      </Flex>
      <Box
        className={cn(
          "hidden flex-col mb-4 gap-2 sm:flex sm:flex-row",
          isMobileFiltersOpen && "flex",
        )}
      >
        <Box textStyle={"h4"} className={"my-0 mb-0 sm:hidden"}>
          Filters
        </Box>
        <VeriOpportunitySelect
          label={isMobile ? "Opportunity" : ""}
          form={form}
          name={"opportunity"}
          usePlacement={false}
          managerId={String(manager.id)}
          pageSize={100}
          isInvestedIn
        />
        <VeriAccountSelect
          label={isMobile ? "Account" : ""}
          form={form}
          name={"account"}
          usePlacement={false}
        />
        <HookFormDateRangeInput
          label={isMobile ? "Date" : ""}
          fromPlaceholder={"From"}
          toPlaceholder={"To"}
          form={form}
          name={"date"}
        />
      </Box>
      <VeriTable
        actionRecordWrapperProps={{
          className: "absolute right-0 bottom-0 sm:static",
        }}
        tableProps={{ my: 0 }}
        columns={{
          filename: {
            label: "Name",
            props: { w: "100%" },
          },
          contentDate: {
            label: "Date",
            renderer: Renderers.Date,
            props: { whiteSpace: "nowrap" },
          },
        }}
        variant={"unstyledRegular"}
        model={documentModel}
        actions={{
          download: {
            icon: DownloadIcon,
            isInlineAction: true,
            callback: async (ids, documents) => {
              return documentModel.downloadDocument(
                ids[0],
                documents[0].filename,
              );
            },
          },
          downloadAll: {
            label: "Download",
            allowBulk: true,
            callback: async (ids, _, table) => {
              table.clearSelection();

              startDownloadJob(ids.map((id) => Number(id)));
            },
          },
        }}
        noRecordsContent={
          <Stack w={"100%"} py={12} alignItems={"center"} spacing={12}>
            <SearchIcon boxSize={20} />
            {hasAnyFilter ? (
              <>
                <Box textStyle={"h5"}>
                  No documents match the selected filters
                </Box>
                <Button onClick={onClearFilters} outline>
                  Clear filters
                </Button>
              </>
            ) : (
              <Box textStyle={"h5"}>No documents available in this section</Box>
            )}
          </Stack>
        }
        apiUrlParams={cleanObjectUndefinedValues({
          manager_id: manager?.id,
          "filter[opportunity_id]": filterOpportunity?.id,
          "filter[account_id]": filterAccount?.id,
          "filter[tag_id]": tagId,
          "filter[start_date]": dates[0],
          "filter[end_date]": dates[1],
          "filter[search]": searchQuery,
          sort: "-content-date,filename",
        })}
        scrollOnTopOnPageChange
        allowBulkSelection
        apiPagination
      />
    </Box>
  );
};
